/**
 * Wire types, mirroring the backend's `to_item()` output.
 *
 * These are the shapes the API actually returns, transcribed from `domain/models.py`,
 * `domain/decision.py` and `domain/playbook.py` rather than guessed. Two conventions carried
 * over deliberately, because silently "fixing" either here would hide a real behaviour:
 *
 *   * Timestamps are UTC ISO 8601 strings. `Alert`/`Case` use a millisecond `...Z` form and
 *     `Decision`/`Action` use `isoformat()` with a `+00:00` offset. Both parse with `new
 *     Date()`; neither is a local time.
 *   * Absent timestamps are the empty string, not null — `Action.to_item` writes `""` for an
 *     unset `executed_at`. Anything reading them must treat `""` as absent.
 */

export type Severity = 'info' | 'low' | 'medium' | 'high' | 'critical';

export const SEVERITY_ORDER: Record<Severity, number> = {
  info: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

export type AlertSource =
  | 'red_canary'
  | 'crowdstrike'
  | 'entra_id'
  | 'defender'
  | 'sentinel'
  | 'umbrella'
  | 'proofpoint'
  | 'mailbox'
  | 'pagerduty'
  | 'manual';

export const SOURCE_LABELS: Record<AlertSource, string> = {
  red_canary: 'Red Canary',
  crowdstrike: 'CrowdStrike',
  entra_id: 'Entra ID',
  defender: 'Defender',
  sentinel: 'Sentinel',
  umbrella: 'Umbrella',
  proofpoint: 'Proofpoint',
  mailbox: 'Mailbox',
  pagerduty: 'PagerDuty',
  manual: 'Manual',
};

export type SubjectKind =
  | 'identity'
  | 'device'
  | 'ip'
  | 'domain'
  | 'url'
  | 'file_hash'
  | 'mailbox';

export interface Subject {
  kind: SubjectKind;
  value: string;
}

export type CaseStatus =
  | 'open'
  | 'enriching'
  | 'awaiting_analysis'
  | 'awaiting_approval'
  | 'containing'
  | 'contained'
  | 'closed_benign'
  | 'closed_resolved'
  | 'failed';

export const TERMINAL_STATUSES: ReadonlySet<CaseStatus> = new Set<CaseStatus>([
  'contained',
  'closed_benign',
  'closed_resolved',
  'failed',
]);

export const STATUS_LABELS: Record<CaseStatus, string> = {
  open: 'Open',
  enriching: 'Enriching',
  awaiting_analysis: 'Awaiting analysis',
  awaiting_approval: 'Awaiting approval',
  containing: 'Containing',
  contained: 'Contained',
  closed_benign: 'Closed — benign',
  closed_resolved: 'Closed — resolved',
  failed: 'Failed',
};

export interface Alert {
  alert_id: string;
  source: AlertSource;
  source_id: string;
  title: string;
  description: string;
  severity: Severity;
  occurred_at: string;
  received_at: string;
  subjects: Subject[];
  tactics: string[];
  source_url: string;
  raw_s3_key: string;
  attributes: Record<string, string>;
}

export interface Case {
  case_id: string;
  status: CaseStatus;
  severity: Severity;
  title: string;
  opened_at: string;
  updated_at: string;
  alert_ids: string[];
  correlation_keys: string[];
  assigned_to: string;
  closed_reason: string;
  /**
   * Which vendors reported this case. Present on the list route only — the detail route
   * returns the alerts themselves, which carry their own source.
   *
   * Derived server-side from the alert ids rather than stored, so it is populated for cases
   * opened before the field existed. Optional here because the case inside `CaseDetail` is
   * the stored shape and does not carry it.
   */
  sources?: AlertSource[];
}

export type Verdict =
  | 'benign'
  | 'suspicious'
  | 'compromise_likely'
  | 'compromise_confirmed'
  | 'insufficient_evidence';

export const VERDICT_LABELS: Record<Verdict, string> = {
  benign: 'Benign',
  suspicious: 'Suspicious',
  compromise_likely: 'Compromise likely',
  compromise_confirmed: 'Compromise confirmed',
  insufficient_evidence: 'Insufficient evidence',
};

export type ActionKind =
  | 'revoke_sessions'
  | 'disable_account'
  | 'enable_account'
  | 'reset_password'
  | 'confirm_compromised'
  | 'remove_inbox_rule'
  | 'disable_forwarding'
  | 'revoke_oauth_grant'
  | 'isolate_host'
  | 'release_host'
  | 'block_domain'
  | 'quarantine_message'
  | 'notify_only';

export const ACTION_LABELS: Record<ActionKind, string> = {
  revoke_sessions: 'Revoke sessions',
  disable_account: 'Disable account',
  enable_account: 'Enable account',
  reset_password: 'Reset password',
  confirm_compromised: 'Confirm compromised',
  remove_inbox_rule: 'Remove inbox rule',
  disable_forwarding: 'Disable forwarding',
  revoke_oauth_grant: 'Revoke OAuth grant',
  isolate_host: 'Isolate host',
  release_host: 'Release host',
  block_domain: 'Block domain',
  quarantine_message: 'Quarantine message',
  notify_only: 'Notify only',
};

export type Mode = 'disabled' | 'approve' | 'auto';

export type ActionStatus =
  | 'recommended'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'executing'
  | 'succeeded'
  | 'failed'
  | 'expired'
  | 'blocked';

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  recommended: 'Recommended',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
  executing: 'Executing',
  succeeded: 'Succeeded',
  failed: 'Failed',
  expired: 'Expired',
  blocked: 'Blocked',
};

export interface RecommendedAction {
  kind: ActionKind;
  target: string;
  rationale: string;
  params: Record<string, string>;
  urgency: string;
}

export interface Decision {
  decision_id: string;
  case_id: string;
  verdict: Verdict;
  /** Serialised as a string by `Decision.to_item`. Display only — never an authority check. */
  confidence: string;
  summary: string;
  reasoning: string;
  assessed_severity: Severity;
  recommended_actions: RecommendedAction[];
  key_evidence: string[];
  intel_gaps: string[];
  /** Instruction-shaped text found in evidence. Non-empty is itself a security signal. */
  injection_observed: string[];
  model_id: string;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  evidence_complete: boolean;
  created_at: string;
}

export interface Action {
  action_id: string;
  case_id: string;
  kind: ActionKind;
  target: string;
  mode: Mode;
  status: ActionStatus;
  rationale: string;
  params: Record<string, string>;
  decision_id: string;
  requested_by: string;
  approved_by: string;
  blocked_reason: string;
  result: string;
  created_at: string;
  /** Empty string when unset. */
  executed_at: string;
  /** Empty string when unset. */
  expires_at: string;
}

/** `GET /api/cases/{id}` — the case with everything hung off it. */
export interface CaseDetail {
  case: Case;
  alerts: Alert[];
  decisions: Decision[];
  actions: Action[];
}

/** `GET /api/cases` */
export interface CaseList {
  cases: Case[];
  /**
   * The sources this deployment ingests from, so the filter offers only real options — the
   * same reasoning as `RunHistory.sources`.
   *
   * Deliberately not the sources present in `cases`: options derived from the rows on screen
   * would disappear as soon as the filter narrowed the list.
   */
  sources: AlertSource[];
  next_cursor?: string;
}

/**
 * `GET /api/cases/{id}/evidence` — the redacted pack, plus what could not be collected.
 *
 * `coverage` is the reason this view exists: `evidence/redact.py` and the collector both
 * report what they could not see, and an analyst reading a verdict needs that in front of
 * them. Silence must not read as cleanliness.
 */
export interface EvidenceCoverage {
  complete: boolean;
  section_error_count: number;
  finding_count: number;
  truncation_markers: string[];
  exchange_connected: boolean;
  /**
   * Whether a collection ran at all. A case naming no identity has no account to audit, so
   * the collector is never started — and every field above then holds its fail-closed
   * default, which reads as a collection that ran and was refused. Check this first: while
   * it is false, nothing else here describes anything that happened.
   */
  attempted?: boolean;
}

export interface EvidenceArtefact {
  kind: string;
  collected_at: string;
  /** Already redacted and sanitised server-side. Never raw collector output. */
  content: string;
  coverage?: EvidenceCoverage;
  /** From `SanitisationReport` — what the sanitiser removed or flagged. */
  injection_markers: string[];
  trailer_removed: boolean;
  trailer_lines_removed: number;
  header_lines_removed: number;
  mutating_lines: number;
  /** pseudonym -> original, for the subjects the analyst is allowed to resolve. */
  pseudonyms: Record<string, string>;
}

export interface EvidencePack {
  case_id: string;
  artefacts: EvidenceArtefact[];
  coverage: EvidenceCoverage;
}

/** `GET /api/health` — admin only. */
export interface SourceHealth {
  source: AlertSource;
  enabled: boolean;
  /**
   * `unconfigured` is the distinction `domain/errors.py` exists to keep: no credential is an
   * unfinished deployment, not a fault. `stale` is the one worth watching — configured,
   * credentialled, and no successful poll in four cadences, which is what a revoked API key
   * looks like from here. `pending` means it has simply never polled yet.
   */
  state: 'ok' | 'stale' | 'pending' | 'unconfigured' | 'failed';
  /**
   * Whether anything polls this source. False for the webhook-only feeds, which is why they
   * are never `stale` and have no countdown: nothing is scheduled, so silence from one is not
   * evidence either way.
   */
  pollable: boolean;
  /** ISO 8601, or empty if this source has never completed a poll. */
  last_success: string;
  /**
   * When a poll last ran, whatever came of it. Distinct from `last_success` and the field the
   * countdown is measured from: a rate schedule fires a fixed interval after the previous
   * invocation and does not care whether it worked.
   */
  last_attempt: string;
  last_error: string;
  cursor: string;
  /**
   * When the schedule should fire next, derived from `last_attempt` plus the interval —
   * EventBridge Scheduler has no API for it. Empty for a source that has never run or is not
   * polled, where the honest answer is unknown rather than a guess.
   */
  next_poll_at: string;
  /** Null for a source with no recorded run, which is not the same as a run that did nothing. */
  last_run: PollRun | null;
}

export interface Health {
  sources: SourceHealth[];
  /** key -> `set` | `placeholder` | `unknown`, from `common/aws.secret_key_status`. */
  secrets: Record<string, Record<string, string>>;
  auto_actions_last_hour: number;
  /** The configured poll cadence, so the countdown ring knows what a full cycle is. */
  poll_interval_seconds: number;
  version: string;
}

/**
 * One recorded poll, from `GET /api/health/runs` — IRA.Admin.
 *
 * The history the liveness record cannot hold. `last_success` says a source is alive; this
 * says whether it is behaving as it did yesterday, which is the next question and a different
 * one. A source whose polls have gone from one page to forty is working and in trouble.
 *
 * Expires after a week: operational telemetry, not the incident record.
 */
export interface PollRun {
  source: AlertSource;
  ok: boolean;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  ingested: number;
  pages: number;
  error: string;
  /** `scheduled` or `manual`. A manual run explains a gap that would look like a missed tick. */
  trigger: string;
}

export interface RunHistory {
  runs: PollRun[];
  /** The pollable sources the history covers, so the filter offers only real options. */
  sources: AlertSource[];
  /**
   * Opaque cursor for the page after this one; empty when there is none.
   *
   * Opaque on purpose — it encodes a `(started_at, source)` pair, because a page spans one
   * table partition per source and the boundary is a position in the merged order that no
   * single partition knows. Pass it back as `after`; do not parse it.
   */
  next: string;
}

/** One line of this platform's own CloudWatch logs. */
export interface LogLine {
  timestamp: string;
  stream: string;
  message: string;
}

/** `GET /api/health/logs` — IRA.Admin. */
export interface LogTail {
  component: string;
  log_group: string;
  source: string;
  /** Oldest first, so the last line is the most recent — a log reads forward. */
  events: LogLine[];
  /**
   * Whether the interval held more than was returned. Surfaced rather than hidden: a window
   * showing 200 lines of an interval that produced 5,000 is not wrong, but presenting it as
   * the whole interval invites the conclusion that nothing else happened.
   */
  truncated: boolean;
  components: string[];
}

/**
 * `GET /api/config` — the policy in force, admin only.
 *
 * The deployed baseline with any runtime override applied on top, not the contents of
 * `cdk.json`. Every action kind appears, including the ones nobody configured: absent means
 * `disabled`, and omitting them would imply the platform had no opinion about an action it
 * will in fact refuse.
 */
export interface PolicyConfig {
  action_modes: Record<string, Mode>;
  protected_principals: string[];
  auto_actions_per_hour: number;
  approval_ttl_hours: number;
  require_complete_evidence_for_auto: boolean;
  evidence_token_budget: number;
  enabled_sources: string[];
}

/** One recorded policy change. Immutable; the history is the audit trail. */
export interface PolicyVersion {
  created_at: string;
  changed_by: string;
  note: string;
  /** Environment-shaped strings, the form the policy loader reads. */
  values: Record<string, string>;
}

/** `GET /api/policy/history` — admin only, newest first. */
export interface PolicyHistory {
  versions: PolicyVersion[];
}

/** `PUT /api/policy` — admin only. Returns the new version and the resulting config. */
export interface PolicyUpdate {
  version: string;
  changed_by: string;
  config: PolicyConfig;
}
